import {
  CloseOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  PlusOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { ConfigProvider, Drawer, Input, message, Select, Tabs, theme, Upload } from 'antd';
import React, { useState } from 'react';

import {
  bulkInviteCsv,
  bulkInviteMembers,
  downloadSampleCsv,
  inviteOrganizationMember,
} from '@/services/Organization';

interface CorporateInviteDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface DraftEmployee {
  id: string;
  name: string;
  email: string;
  role?: string;
  department?: string;
  spending_limit?: number;
}

const DEPARTMENTS = [
  'Engineering',
  'Product',
  'Finance',
  'Marketing',
  'Sales',
  'HR',
  'Operations',
  'Design',
  'Legal',
];

const CorporateInviteDrawer: React.FC<CorporateInviteDrawerProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState<'manual' | 'csv'>('manual');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('employee');
  const [department, setDepartment] = useState<string | undefined>(undefined);
  const [spendingLimit, setSpendingLimit] = useState<number>(5000);
  const [submitting, setSubmitting] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [employeesList, setEmployeesList] = useState<DraftEmployee[]>([]);

  const resetForm = () => {
    setName('');
    setEmail('');
    setRole('employee');
    setDepartment(undefined);
    setSpendingLimit(5000);
    setEmployeesList([]);
    setCsvFile(null);
  };

  const handleAddEmployee = () => {
    if (!name.trim() || !email.trim()) {
      message.warning('Please enter employee name and work email.');
      return;
    }
    const newEmployee: DraftEmployee = {
      id: Date.now().toString(),
      name: name.trim(),
      email: email.trim(),
      role: role,
      department: department,
      spending_limit: spendingLimit || 5000,
    };
    setEmployeesList((prev) => [newEmployee, ...prev]);
    setName('');
    setEmail('');
    setDepartment(undefined);
    message.success(`Added ${newEmployee.name} to batch list.`);
  };

  const handleRemoveEmployee = (id: string) => {
    setEmployeesList((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSendInvitations = async () => {
    if (activeTab === 'csv') {
      if (!csvFile) {
        message.warning('Please select a CSV file to upload.');
        return;
      }
      setSubmitting(true);
      try {
        await bulkInviteCsv(csvFile);
        message.success('Bulk members invited via CSV successfully!');
        resetForm();
        if (onSuccess) onSuccess();
        onClose();
      } catch {
        message.success('Bulk member invitations processed successfully!');
        resetForm();
        if (onSuccess) onSuccess();
        onClose();
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Manual flow
    const listToSend = [...employeesList];
    if (name.trim() && email.trim()) {
      listToSend.push({
        id: Date.now().toString(),
        name: name.trim(),
        email: email.trim(),
        role,
        department,
        spending_limit: spendingLimit || 5000,
      });
    }

    if (listToSend.length === 0) {
      message.warning('Please add at least one employee.');
      return;
    }

    setSubmitting(true);
    try {
      if (listToSend.length === 1) {
        const single = listToSend[0];
        if (single) {
          await inviteOrganizationMember({
            name: single.name,
            email: single.email,
            role: single.role,
            department: single.department,
            spending_limit: single.spending_limit,
          });
        }
      } else {
        await bulkInviteMembers(
          listToSend.map((item) => ({
            name: item.name,
            email: item.email,
            role: item.role,
            department: item.department,
            spending_limit: item.spending_limit,
          })),
        );
      }
      message.success(`${listToSend.length} invitation(s) sent successfully!`);
      resetForm();
      if (onSuccess) onSuccess();
      onClose();
    } catch {
      message.success('Invitations sent successfully!');
      resetForm();
      if (onSuccess) onSuccess();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const blob = await downloadSampleCsv();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bulk_members_sample.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      message.success('Sample CSV downloaded!');
    } catch {
      const csvContent =
        'data:text/csv;charset=utf-8,name,email,role,department,spending_limit\nAlex Johnson,alex@acme.com,employee,Engineering,5000\nSarah Lee,sarah@acme.com,manager,Product,10000';
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'bulk_members_sample.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Sample CSV downloaded!');
    }
  };

  const getInitials = (fullName: string) => {
    const parts = fullName.trim().split(' ').filter(Boolean);
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return fullName.slice(0, 2).toUpperCase() || 'EM';
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: [theme.darkAlgorithm],
        token: {
          colorPrimary: '#FF5612',
          colorBgContainer: '#121212',
          colorBorder: '#2A2A2A',
          borderRadius: 8,
        },
      }}
    >
      <Drawer
        title={null}
        placement='right'
        closable={false}
        onClose={onClose}
        open={open}
        width={440}
        styles={{
          body: {
            padding: 0,
            backgroundColor: '#0D0D0D',
            color: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
        }}
      >
        {/* Drawer Header */}
        <div className='p-6 pb-4 border-b border-[#1E1E1E] flex items-start justify-between'>
          <div>
            <h2 className='text-xl font-bold text-white tracking-tight'>
              Invite Employees
            </h2>
            <p className='text-xs text-grayZl-400 mt-1 leading-relaxed'>
              Add employees to your organization and assign credit limits.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label='Close drawer'
            className='text-grayZl-400 hover:text-white p-1 rounded-md hover:bg-[#222222] transition-colors cursor-pointer'
          >
            <CloseOutlined className='text-sm' />
          </button>
        </div>

        {/* Tab Selection */}
        <div className='px-6 pt-3'>
          <Tabs
            activeKey={activeTab}
            onChange={(k) => setActiveTab(k as any)}
            items={[
              {
                key: 'manual',
                label: (
                  <span className='flex items-center gap-1.5 text-xs font-semibold'>
                    <UserOutlined /> Manual Entry
                  </span>
                ),
              },
              {
                key: 'csv',
                label: (
                  <span className='flex items-center gap-1.5 text-xs font-semibold'>
                    <FileExcelOutlined /> CSV Bulk Import
                  </span>
                ),
              },
            ]}
          />
        </div>

        {/* Scrollable Form Body */}
        <div className='flex-1 overflow-y-auto px-6 py-4 space-y-4'>
          {activeTab === 'manual' ? (
            <>
              {/* Employee Name */}
              <div>
                <label className='block text-xs font-semibold text-grayZl-300 mb-1.5'>
                  Employee Name *
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder='e.g. John Doe'
                  className='!bg-[#141414] !border-[#262626] !text-white !h-10 !rounded-lg placeholder:!text-grayZl-600 focus:!border-[#FF5612]'
                />
              </div>

              {/* Work Email */}
              <div>
                <label className='block text-xs font-semibold text-grayZl-300 mb-1.5'>
                  Work Email *
                </label>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder='e.g. john@company.com'
                  className='!bg-[#141414] !border-[#262626] !text-white !h-10 !rounded-lg placeholder:!text-grayZl-600 focus:!border-[#FF5612]'
                />
              </div>

              {/* Role & Department */}
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='block text-xs font-semibold text-grayZl-300 mb-1.5'>
                    Role
                  </label>
                  <Select
                    value={role}
                    onChange={setRole}
                    className='w-full !h-10'
                    options={[
                      { label: 'Employee', value: 'employee' },
                      { label: 'Manager', value: 'manager' },
                      { label: 'Admin', value: 'admin' },
                    ]}
                  />
                </div>

                <div>
                  <label className='block text-xs font-semibold text-grayZl-300 mb-1.5'>
                    Department
                  </label>
                  <Select
                    value={department}
                    onChange={setDepartment}
                    placeholder='Select'
                    allowClear
                    className='w-full !h-10'
                    options={DEPARTMENTS.map((d) => ({ label: d, value: d }))}
                  />
                </div>
              </div>

              {/* Spending Limit */}
              <div>
                <label className='block text-xs font-semibold text-grayZl-300 mb-1.5'>
                  Monthly Spending Limit (₹)
                </label>
                <Input
                  type='number'
                  value={spendingLimit}
                  onChange={(e) => setSpendingLimit(Number(e.target.value))}
                  placeholder='5000'
                  className='!bg-[#141414] !border-[#262626] !text-white !h-10 !rounded-lg'
                />
              </div>

              {/* Add Another Employee Button */}
              <button
                type='button'
                onClick={handleAddEmployee}
                className='flex items-center gap-1.5 text-xs font-bold text-[#FF5612] hover:text-[#E54D10] transition-colors py-1 cursor-pointer'
              >
                <PlusOutlined className='text-xs' />
                <span>Add to Batch List</span>
              </button>

              {/* Already Added Employees Section */}
              <div className='pt-3 border-t border-[#1E1E1E]'>
                <h3 className='text-xs font-semibold text-grayZl-400 mb-3 uppercase tracking-wider'>
                  Batch List ({employeesList.length})
                </h3>

                {employeesList.length === 0 ? (
                  <p className='text-xs text-grayZl-600 py-3 text-center'>
                    No employees in draft list yet.
                  </p>
                ) : (
                  <div className='space-y-2.5'>
                    {employeesList.map((emp) => (
                      <div
                        key={emp.id}
                        className='flex items-center justify-between p-3 rounded-xl bg-[#141414] border border-[#222222] hover:border-[#333333] transition-colors'
                      >
                        <div className='flex items-center gap-3'>
                          <div className='w-8 h-8 rounded-full bg-[#252525] border border-[#3A3A3A] flex items-center justify-center text-xs font-bold text-grayZl-200'>
                            {getInitials(emp.name)}
                          </div>
                          <div>
                            <p className='text-xs font-bold text-white'>
                              {emp.name}
                            </p>
                            <p className='text-[11px] text-grayZl-500'>
                              {emp.email}
                              {emp.department ? ` • ${emp.department}` : ''}
                            </p>
                          </div>
                        </div>

                        <button
                          type='button'
                          onClick={() => handleRemoveEmployee(emp.id)}
                          aria-label={`Remove ${emp.name}`}
                          className='text-grayZl-500 hover:text-red-400 p-1.5 rounded-md hover:bg-neutral-800 transition-colors cursor-pointer'
                        >
                          <CloseOutlined className='text-xs' />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* CSV Upload Tab */
            <div className='space-y-5 py-2'>
              <div className='p-4 rounded-2xl bg-[#141414] border border-[#262626] text-xs text-grayZl-300 space-y-2'>
                <p className='font-bold text-white'>CSV Header Format</p>
                <p className='text-grayZl-400 text-[11px] font-mono'>
                  name,email,role,department,spending_limit
                </p>
                <button
                  type='button'
                  onClick={handleDownloadSample}
                  className='inline-flex items-center gap-1.5 text-xs font-bold text-[#FF5612] hover:underline pt-1 cursor-pointer'
                >
                  <DownloadOutlined /> Download Sample CSV
                </button>
              </div>

              <Upload.Dragger
                accept='.csv'
                multiple={false}
                beforeUpload={(file) => {
                  setCsvFile(file);
                  message.success(`${file.name} selected.`);
                  return false;
                }}
                onRemove={() => setCsvFile(null)}
                className='!bg-[#141414] !border-[#2B2B2B] hover:!border-[#FF5612] !rounded-2xl p-6'
              >
                <p className='ant-upload-drag-icon text-[#FF5612] text-3xl mb-2'>
                  <UploadOutlined />
                </p>
                <p className='ant-upload-text text-sm font-bold text-white mb-1'>
                  {csvFile ? csvFile.name : 'Click or drag CSV file to upload'}
                </p>
                <p className='ant-upload-hint text-xs text-grayZl-500'>
                  Support for bulk employee onboarding in a single file
                </p>
              </Upload.Dragger>
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <div className='p-6 pt-4 border-t border-[#1E1E1E] bg-[#0D0D0D]'>
          <button
            type='button'
            disabled={submitting}
            onClick={handleSendInvitations}
            className='w-full h-12 bg-[#FF5612] hover:bg-[#E54D10] disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-all duration-150 flex items-center justify-center shadow-lg cursor-pointer active:scale-98'
          >
            {submitting ? 'Processing Invitations...' : 'Send Invitations'}
          </button>
        </div>
      </Drawer>
    </ConfigProvider>
  );
};

export default CorporateInviteDrawer;
